import * as React from "react";
import {Link} from "react-router-dom";
import Slider, {Settings} from "react-slick";
import {intervalString, PastCaseProject,} from "src/domain/model/past_case_project";
import {Multi_SkillTag, Single_SkillTag} from "src/domain/model/skill_tag";
import {createApiClient, IApiClient} from "src/infra/api/client";
import {entity} from "src/infra/api/rpc/api";
import {createPublicAPI} from "src/infra/api/service/public_api";
import SlideNextArrow from "src/presentation/common/atom/slider_arrow/slide_next_arrow";
import SlidePrevArrow from "src/presentation/common/atom/slider_arrow/slide_prev_arrow";
import HeadlineSmall from "src/presentation/common/atom/text/headline_small";
import ReviewList from "src/presentation/component/client/customer/review_list";
import SlideCustomerCard from "src/presentation/component/client/top_page/slide_customer_card";
import styled from "styled-components";
import {
    customerConfig,
    jobCareerConfig,
    pastCaseProjectConfig,
    skillTagConfig,
} from "../../../../config/language/ja-JP/domain/model_text";
import {ConstBreakPoint, ConstCalc, ConstColor,} from "../../../../config/styles_const/styles_const";
import {Company} from "../../../../domain/model/company";
import {Customer, Language} from "../../../../domain/model/customer";
import {JobCareer} from "../../../../domain/model/job_career";
import {SingleSkillTag} from "../../../../domain/model/skill_tag";
import {useOnScreen} from "../../../../presentation/on-screen";
import Assets from "../../../assets/img/assets";
import HeadlineMiddle from "../../../common/atom/text/headline_middle";
import CustomerInterviewMemoInfo from "../../../common/template/customer_meta/customer_interview_memo_info";
import {scrollTo} from "../../../scroll_to";
import CustomerProfile from "./customer_profile";

const settings: Settings = {
    className: "slider",
    dots: false,
    infinite: false,
    centerMode: false,
    swipeToSlide: true,
    variableWidth: true,
    arrows: true,
    prevArrow: <SlidePrevArrow/>,
    nextArrow: <SlideNextArrow/>,
    slidesToShow: 4,
    slidesToScroll: 4,
    responsive: [
        {
            breakpoint: 1024,
            settings: {
                slidesToShow: 3,
                slidesToScroll: 3,
            },
        },
        {
            breakpoint: 650,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 2,
            },
        },

        {
            breakpoint: 450,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
            },
        },
    ],
};

interface IProps {
    customer: Customer | null;
    jobCareers: JobCareer[];
    pastCaseProjects: PastCaseProject[];
    company: Company | null;
    onClickLinkAsPublic?: () => void;
}

const apiClient: IApiClient = createApiClient();
const publicAPI = createPublicAPI(apiClient);

export const ClientCustomerMainV2: React.FC<IProps> = (props) => {
    const {
        customer,
        jobCareers,
        pastCaseProjects,
        company,
        onClickLinkAsPublic,
    } = props;

    if (!customer) {
        return <div/>;
    }

    const [matches, setMatches] = React.useState<Customer[] | undefined>(
        undefined
    );
    const [trends, setTrends] = React.useState<Customer[] | undefined>(undefined);
    const [reviews, setReviews] = React.useState<entity.IReview[]>([]);

    React.useEffect(() => {
        try {
            publicAPI.getRecommmendCustomerByMatch(customer.id).then((items) => {
                setMatches(items);
            });

            publicAPI.getRecommmendCustomerByTrend(customer.id).then((items) => {
                setTrends(items);
            });

            publicAPI.getAllReview(customer.id, 1, 10000).then((items) => {
                setReviews(items);
            });
        } catch (e) {

        }
    }, []);

    const createLanguageList: string[] = customer.languages.map(
        (item: Language) => {
            return customerConfig.languageList[item];
        }
    );

    const tagsByCategory = (category: Multi_SkillTag.Category) => {
        const tags = customer.multiSkillTags.filter(
            (tag) => tag.category === category
        );
        if (
            category !== Multi_SkillTag.Category.Category_SpecialtyIndustry &&
            category !== Multi_SkillTag.Category.Category_CXO &&
            tags.length === 0
        ) {
            return <></>;
        }

        return (
            <>
                <TagCategory>
                    {skillTagConfig.multiSkillTagCategoryList.get(category)}
                </TagCategory>
                <TagList>
                    {tags.slice(0, 2).map((v) => {
                        if (company) {
                            return (
                                <Link
                                    key={v.id}
                                    style={{color: "#9a8c64"}}
                                    to={`/client/scout/customers?mt=${v.id}&mc=${v.category}`}
                                >
                                    <Tag>{v.name}</Tag>
                                </Link>
                            );
                        } else {
                            return (
                                <span
                                    key={v.id}
                                    style={{color: "#9a8c64", cursor: "pointer"}}
                                    onClick={() => {
                                        if (onClickLinkAsPublic) {
                                            onClickLinkAsPublic();
                                        }
                                    }}
                                >
                  <Tag>{v.name}</Tag>
                </span>
                            );
                        }
                    })}
                </TagList>
            </>
        );
    };

    const [tagEllipsis, setTagEllipsis] = React.useState<boolean>(
        customer.multiSkillTags.filter((v) => {
            switch (v.category) {
                case Multi_SkillTag.Category.Category_SpecialtyIndustry:
                case Multi_SkillTag.Category.Category_CXO:
                    return false;
                default:
                    return true;
            }
        }).length >= 3
    );

    const singleTagsByCategory = (category: Single_SkillTag.Category) => {
        const tags = customer.singleSkillTags.filter(
            (tag) => tag.category === category
        );
        if (tags.length === 0) {
            return <></>;
        }

        const getLink = (tag: SingleSkillTag): string => {
            switch (category) {
                case Single_SkillTag.Category.Category_GoodPhase:
                    return `/client/scout/customers?st1=${tag.id}`;
                case Single_SkillTag.Category.Category_Fit:
                    return `/client/scout/customers?st2=${tag.id}`;
                case Single_SkillTag.Category.Category_GoodServiceClassification:
                    return `/client/scout/customers?st3=${tag.id}`;
                default:
                    return "";
            }
        };

        return (
            <>
                <TagCategory>
                    {skillTagConfig.singleSkillTagCategoryList.get(category)}
                </TagCategory>
                <TagList>
                    {tags.slice(0, 2).map((v) => {
                        if (company) {
                            return (
                                <Link key={v.id} style={{color: "#9a8c64"}} to={getLink(v)}>
                                    <Tag>{v.name}</Tag>
                                </Link>
                            );
                        } else {
                            return (
                                <span
                                    style={{cursor: "pointer"}}
                                    onClick={() => {
                                        if (onClickLinkAsPublic) {
                                            onClickLinkAsPublic();
                                        }
                                    }}
                                >
                  <Tag>{v.name}</Tag>
                </span>
                            );
                        }
                    })}
                </TagList>
            </>
        );
    };

    const topRef: React.MutableRefObject<HTMLDivElement | null> =
        React.useRef<HTMLDivElement | null>(null);
    const skillRef: React.MutableRefObject<HTMLDivElement | null> =
        React.useRef<HTMLDivElement | null>(null);
    const careerRef: React.MutableRefObject<HTMLDivElement | null> =
        React.useRef<HTMLDivElement | null>(null);
    const pastProjectRef: React.MutableRefObject<HTMLDivElement | null> =
        React.useRef<HTMLDivElement | null>(null);
    const otherRef: React.MutableRefObject<HTMLDivElement | null> =
        React.useRef<HTMLDivElement | null>(null);
    const interviewRef: React.MutableRefObject<HTMLDivElement | null> =
        React.useRef<HTMLDivElement | null>(null);
    const relatedRef: React.MutableRefObject<HTMLDivElement | null> =
        React.useRef<HTMLDivElement | null>(null);
    const reviewRef: React.MutableRefObject<HTMLDivElement | null> =
        React.useRef<HTMLDivElement | null>(null);

    const topVisible = useOnScreen(topRef);
    const skillVisible = useOnScreen(skillRef);
    const careerVisible = useOnScreen(careerRef);
    const pastProjectVisible = useOnScreen(pastProjectRef);
    const otherVisible = useOnScreen(otherRef);
    const interviewVisible = useOnScreen(interviewRef);
    const relatedVisible = useOnScreen(relatedRef);

    let res: JSX.Element;

    // 通常ページ

    res = (
        <React.Fragment>
            <LinkMenu>
                <ScrollLink
                    isActive={topVisible}
                    onClick={() => {
                        scrollTo({ref: topRef, duration: 1000});
                    }}
                >
                    トップ
                </ScrollLink>
                <ScrollLink
                    isActive={skillVisible && !topVisible}
                    onClick={() => {
                        scrollTo({ref: skillRef, duration: 1000});
                    }}
                >
                    スキル・経験
                </ScrollLink>
                <ScrollLink
                    isActive={careerVisible && !topVisible && !skillVisible}
                    onClick={() => {
                        scrollTo({ref: careerRef, duration: 1000});
                    }}
                >
                    キャリア
                </ScrollLink>
                <ScrollLink
                    isActive={
                        pastProjectVisible && !topVisible && !skillVisible && !careerVisible
                    }
                    onClick={() => {
                        scrollTo({ref: pastProjectRef, duration: 1000});
                    }}
                >
                    稼働イメージ
                </ScrollLink>
                <ScrollLink
                    isActive={
                        otherVisible &&
                        !topVisible &&
                        !skillVisible &&
                        !careerVisible &&
                        !pastProjectVisible
                    }
                    onClick={() => {
                        scrollTo({ref: otherRef, duration: 1000});
                    }}
                >
                    その他
                </ScrollLink>
                <ScrollLink
                    isActive={
                        interviewVisible &&
                        !topVisible &&
                        !skillVisible &&
                        !careerVisible &&
                        !pastProjectVisible &&
                        !otherVisible
                    }
                    onClick={() => {
                        scrollTo({ref: interviewRef, duration: 1000});
                    }}
                >
                    面談情報
                </ScrollLink>
                <ScrollLink
                    isActive={
                        relatedVisible &&
                        !topVisible &&
                        !skillVisible &&
                        !careerVisible &&
                        !pastProjectVisible &&
                        !otherVisible &&
                        !interviewVisible
                    }
                    onClick={() => {
                        scrollTo({ref: relatedRef, duration: 1000});
                    }}
                >
                    関連エキスパート
                </ScrollLink>
            </LinkMenu>

            <div ref={topRef}>
                <CustomerProfile
                    customer={customer}
                    company={company}
                    avarageReviewPoint={
                        reviews.reduce(
                            (sum, current) => sum + (current.point ? current.point : 0),
                            0
                        ) / reviews.length
                    }
                    onTapReviewLink={() => {
                        scrollTo({ref: reviewRef, duration: 1000});
                    }}
                />
            </div>

            <Divider/>

            <div ref={skillRef}>
                <HeadlineMiddle>スキル・経験</HeadlineMiddle>
                <TagSection>
                    <TagSectionInner>
                        <TagSectionInnerLeft>
                            <SingleTagSectionInner>
                                <SingleTagSectionInnerLeft>
                                    {singleTagsByCategory(
                                        Single_SkillTag.Category.Category_GoodPhase
                                    )}
                                </SingleTagSectionInnerLeft>
                                <SingleTagSectionInnerRight>
                                    {singleTagsByCategory(Single_SkillTag.Category.Category_Fit)}
                                </SingleTagSectionInnerRight>
                            </SingleTagSectionInner>
                            {tagsByCategory(
                                Multi_SkillTag.Category.Category_SpecialtyIndustry
                            )}
                            {!tagEllipsis && (
                                <>
                                    {tagsByCategory(
                                        Multi_SkillTag.Category.Category_OverseasExpansion
                                    )}
                                    {tagsByCategory(Multi_SkillTag.Category.Category_Programming)}
                                    {tagsByCategory(Multi_SkillTag.Category.Category_AI)}
                                    {tagsByCategory(Multi_SkillTag.Category.Category_Other)}
                                </>
                            )}
                        </TagSectionInnerLeft>
                        <TagSectionInnerRight>
                            <SingleTagSectionInner>
                                <SingleTagSectionInnerLeft>
                                    {singleTagsByCategory(
                                        Single_SkillTag.Category.Category_GoodServiceClassification
                                    )}
                                </SingleTagSectionInnerLeft>
                            </SingleTagSectionInner>
                            {tagsByCategory(Multi_SkillTag.Category.Category_CXO)}
                            {!tagEllipsis && (
                                <>
                                    {tagsByCategory(
                                        Multi_SkillTag.Category.Category_IntroductionOperation
                                    )}
                                    {tagsByCategory(
                                        Multi_SkillTag.Category.Category_PersonalConnections
                                    )}
                                    {tagsByCategory(
                                        Multi_SkillTag.Category.Category_DevelopFramework
                                    )}
                                </>
                            )}
                        </TagSectionInnerRight>
                    </TagSectionInner>

                    {tagEllipsis && (
                        <ReadMore
                            onClick={() => {
                                setTagEllipsis(false);
                            }}
                        >
                            全て表示
                        </ReadMore>
                    )}
                </TagSection>
            </div>

            <Divider/>

            <div ref={careerRef}>
                <JobCareerSection jobCareers={jobCareers} customer={customer}/>
            </div>

            <Divider/>

            <div ref={pastProjectRef}>
                <PastProjectSection
                    pastCaseProjects={pastCaseProjects}
                    customer={customer}
                />
            </div>

            <Divider/>

            <div ref={otherRef}>
                <HeadlineMiddle>その他情報</HeadlineMiddle>
                <Section>
                    <div style={{flex: "1", padding: "0px 20px"}}>
                        <InfoArea>
                            <div style={{display: "flex", marginBottom: 16}}>
                                <InfoLabel>所在地</InfoLabel>
                                <InfoValue>{customer.address || "---"}</InfoValue>
                            </div>
                            <div style={{display: "flex", marginBottom: 16}}>
                                <InfoLabel>学歴</InfoLabel>
                                <InfoValue>
                                    {customer.academicBackgrounds.toString() || "---"}
                                </InfoValue>
                            </div>
                            <div style={{display: "flex", marginBottom: 16}}>
                                <InfoLabel>資格</InfoLabel>
                                <InfoValue>
                                    {customer.requirements.toString() || "---"}
                                </InfoValue>
                            </div>
                            <div style={{display: "flex", marginBottom: 16}}>
                                <InfoLabel>語学</InfoLabel>
                                <InfoValue>{createLanguageList.toString() || "---"}</InfoValue>
                            </div>
                            <div style={{display: "flex", marginBottom: 16}}>
                                <InfoLabel>ポートフォリオ</InfoLabel>
                                <InfoValue>
                                    <a href={customer.portfolio} target="_blank">
                                        {customer.portfolio}
                                    </a>
                                </InfoValue>
                            </div>
                            <div style={{display: "flex", marginBottom: 16}}>
                                <InfoLabel>出身地</InfoLabel>
                                <InfoValue>{customer.birthplace}</InfoValue>
                            </div>
                        </InfoArea>
                    </div>
                </Section>
            </div>

            <Divider/>

            <div ref={interviewRef}>
                <CustomerInterviewMemoInfo customer={customer} company={company}/>
            </div>

            <div style={{marginTop: 50}}/>
            <Divider/>

            <div ref={reviewRef}>
                {reviews && reviews.length > 0 && (
                    <>
                        <ReviewList reviews={reviews}/>

                        <div style={{marginTop: 50}}/>
                        <Divider/>
                    </>
                )}
            </div>

            <div ref={relatedRef}>
                {matches && matches.length > 0 && (
                    <div>
                        <SubTitleWrapper>
                            <HeadlineSmall>AIが選出した類似のエキスパート</HeadlineSmall>
                        </SubTitleWrapper>

                        <SliderWrapper>
                            <Slider {...settings}>
                                {matches.map((c) => {
                                    return (
                                        <SlideCustomerWrapper key={c.id} style={{width: 200}}>
                                            <SlideCustomerCard
                                                customer={c}
                                                jobCareers={jobCareers}
                                                isBookmarked={false}
                                            />
                                        </SlideCustomerWrapper>
                                    );
                                })}
                            </Slider>
                        </SliderWrapper>
                    </div>
                )}

                {trends && trends.length > 0 && (
                    <div>
                        <SubTitleWrapper>
                            <HeadlineSmall>同じ職種の新着エキスパート</HeadlineSmall>
                        </SubTitleWrapper>

                        <SliderWrapper>
                            <Slider {...settings}>
                                {trends.map((c) => {
                                    return (
                                        <SlideCustomerWrapper key={c.id} style={{width: 200}}>
                                            <SlideCustomerCard
                                                customer={c}
                                                jobCareers={jobCareers}
                                                isBookmarked={false}
                                            />
                                        </SlideCustomerWrapper>
                                    );
                                })}
                            </Slider>
                        </SliderWrapper>
                    </div>
                )}
            </div>
        </React.Fragment>
    );

    return res;
};

export default ClientCustomerMainV2;

interface IJobCareerProps {
    customer: Customer;
    jobCareers: JobCareer[];
}

const JobCareerSection: React.FC<IJobCareerProps> = (props) => {
    const {customer, jobCareers} = props;

    const sortedJobCareers = jobCareers.slice().sort((a, b) => {
        const aStartedNumber =
            Number(a.startedAt.year) * 100 + Number(a.startedAt.month || 0);
        const bStartedNumber =
            Number(b.startedAt.year) * 100 + Number(b.startedAt.month || 0);
        return bStartedNumber - aStartedNumber;
    });

    let lastId = "";
    if (sortedJobCareers.length > 0) {
        lastId = sortedJobCareers[sortedJobCareers.length - 1].id;
    }

    const ellipsisWith = 1;
    const [ellipsis, setEllipsis] = React.useState<boolean>(
        false // sortedJobCareers.length > ellipsisWith
    );

    return (
        <div>
            <HeadlineMiddle>キャリア</HeadlineMiddle>
            <TabSectionContainer>
                <HeadlineSmall>職務要約</HeadlineSmall>
                <div
                    style={{
                        marginTop: 10,
                        marginBottom: 20,
                        fontSize: 15,
                        fontWeight: "normal",
                        color: "#333333",
                        whiteSpace: "pre-wrap",
                        lineHeight: "24px",
                    }}
                >
                    {customer.dutySummary}
                </div>
                {(ellipsis
                        ? sortedJobCareers.slice(0, ellipsisWith)
                        : sortedJobCareers
                ).map((jobCareer: JobCareer) => {
                    return (
                        <div key={jobCareer.id}>
                            <ItemInner>
                                <JobCareerRange>{`${jobCareer.getStartedAtString()}~${jobCareer.getEndedAtString()}`}</JobCareerRange>
                                <div style={{display: "flex"}}>
                                    <Img src={Assets.building}/>
                                    <ItemTitle>{jobCareer.companyName}</ItemTitle>
                                </div>
                                <JobCareerPost>
                                    {jobCareer.post} |{" "}
                                    {jobCareerConfig.employmentStatusList.get(
                                        jobCareer.employmentStatus
                                    )}
                                </JobCareerPost>
                                <JobCareerDescription>
                                    {jobCareer.description}
                                </JobCareerDescription>
                            </ItemInner>
                            {!ellipsis && lastId !== jobCareer.id && (
                                <div
                                    style={{
                                        width: 1,
                                        height: 30,
                                        margin: "0px 20px",
                                        backgroundColor: "#9A8C64",
                                    }}
                                />
                            )}
                        </div>
                    );
                })}
            </TabSectionContainer>

            {ellipsis && (
                <ReadMore
                    onClick={() => {
                        setEllipsis(false);
                    }}
                >
                    全て表示
                </ReadMore>
            )}
        </div>
    );
};

interface IPastProjectProps {
    customer: Customer;
    pastCaseProjects: PastCaseProject[];
}

const PastProjectSection: React.FC<IPastProjectProps> = (props) => {
    const {customer, pastCaseProjects} = props;

    let lastId = "";
    if (pastCaseProjects.length > 0) {
        lastId = pastCaseProjects[pastCaseProjects.length - 1].id;
    }

    const ellipsisWith = 1;
    const [ellipsis, setEllipsis] = React.useState<boolean>(
        pastCaseProjects.length > ellipsisWith
    );

    return (
        <div>
            <HeadlineMiddle>稼働イメージ</HeadlineMiddle>
            <TabSectionContainer>
                <PastProjectInfoContainer>
                    <div style={{flex: "1"}}>
                        <InfoArea>
                            <div style={{display: "flex", marginBottom: 16}}>
                                <InfoLabel>稼働頻度</InfoLabel>
                                <InfoValue>
                                    {customerConfig.workingFrequencyList[
                                        customer.workingFrequency
                                        ] || "---"}
                                </InfoValue>
                            </div>
                            <div style={{display: "flex", marginBottom: 16}}>
                                <InfoLabel>報酬目安</InfoLabel>
                                <InfoValue>
                                    {customerConfig.requestedFeeList[customer.requestedFee] ||
                                        "---"}
                                </InfoValue>
                            </div>
                            <div style={{display: "flex", marginBottom: 16}}>
                                <InfoLabel>その他条件</InfoLabel>
                                <InfoValue>{customer.requestedCondition || "---"}</InfoValue>
                            </div>
                        </InfoArea>
                    </div>
                    <div style={{flex: "1"}}>
                        <InfoArea>
                            <div style={{display: "flex", marginBottom: 16}}>
                                <InfoLabel>稼働スタンス</InfoLabel>
                                <InfoValue>
                                    {customerConfig.workIntentionList.get(
                                        customer.workIntention
                                    ) || "---"}
                                </InfoValue>
                            </div>
                            <div style={{display: "flex", marginBottom: 16}}>
                                <InfoLabel>正社員の可能性</InfoLabel>
                                <InfoValue>
                                    {customerConfig.fullTimePossibilityList.get(
                                        customer.fullTimePossibility
                                    ) || "---"}
                                </InfoValue>
                            </div>
                            <div style={{display: "flex", marginBottom: 16}}>
                                <InfoLabel>仲介案件</InfoLabel>
                                <InfoValue>
                                    {customerConfig.brokerageProjectPossibilityList.get(
                                        customer.brokerageProjectPossibility
                                    ) || "---"}
                                </InfoValue>
                            </div>
                        </InfoArea>
                    </div>
                </PastProjectInfoContainer>
                <div style={{marginBottom: 20, marginTop: 40}}>
                    {/* <HeadlineMiddle>過去の契約例</HeadlineMiddle> */}
                </div>
                {(ellipsis
                        ? pastCaseProjects.slice(0, ellipsisWith)
                        : pastCaseProjects
                ).map((project: PastCaseProject) => {
                    return (
                        <div key={project.id}>
                            <ItemInner>
                                <PastProjectContent>
                                    <div style={{flex: 1}}>
                                        <div style={{display: "flex"}}>
                                            <Img src={Assets.past_project}/>
                                            <ItemTitle>
                                                {project.title} - {project.feePerMonth}万円/月
                                            </ItemTitle>
                                        </div>
                                        <PastProjectDescription>
                                            {`約${project.meetingNum}回/${intervalString(
                                                project.meetingInterval
                                            )}の頻度で、1回あたり約${
                                                project.meetingMinutes
                                            }分の打ち合わせ\n約${
                                                project.workingHour
                                            }時間/${intervalString(project.workingInterval)}の実稼働`}
                                        </PastProjectDescription>
                                    </div>
                                    <div style={{flex: 1}}>
                                        <InfoArea>
                                            <div style={{display: "flex", marginBottom: 16}}>
                                                <PastProjectInfoLabel>稼働方法</PastProjectInfoLabel>
                                                <InfoValue>
                                                    {pastCaseProjectConfig.workLocationList.get(
                                                        project.workLocation
                                                    ) || "---"}
                                                </InfoValue>
                                            </div>
                                            <div style={{display: "flex", marginBottom: 16}}>
                                                <PastProjectInfoLabel>
                                                    コミュニケーション
                                                </PastProjectInfoLabel>
                                                <InfoValue>
                                                    {pastCaseProjectConfig.workCommunicationList.get(
                                                        project.workCommunication
                                                    ) || "---"}
                                                </InfoValue>
                                            </div>
                                            <div style={{display: "flex", marginBottom: 16}}>
                                                <PastProjectInfoLabel>稼働内容</PastProjectInfoLabel>
                                                <InfoValue>
                                                    {pastCaseProjectConfig.workDetailList.get(
                                                        project.workDetail
                                                    ) || "---"}
                                                </InfoValue>
                                            </div>
                                        </InfoArea>
                                    </div>
                                </PastProjectContent>
                                <PastProjectReward>
                                    成果報酬
                                    <br/>
                                    {project.achievementReward}
                                </PastProjectReward>
                            </ItemInner>
                            {!ellipsis && lastId !== project.id && (
                                <div
                                    style={{
                                        width: 1,
                                        height: 30,
                                        margin: "0px 20px",
                                        marginBottom: "10px",
                                        backgroundColor: "#9A8C64",
                                    }}
                                />
                            )}
                        </div>
                    );
                })}
            </TabSectionContainer>

            {ellipsis && (
                <ReadMore
                    onClick={() => {
                        setEllipsis(false);
                    }}
                >
                    全て表示
                </ReadMore>
            )}
        </div>
    );
};

const Section = styled.div`
    display: block;
    text-align: center;
    margin-bottom: 40px;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        display: flex;
        text-align: left;
    }
`;

const InfoTitle = styled.div`
    font-size: 18px;
    font-weight: bold;
    color: #333333;
    text-align: left;
`;

const ButtonWrapper = styled.div`
    margin-left: 10px;
`;

const TabContainer = styled.div`
    display: flex;
    border: solid 1px #ebf0f1;
    border-radius: 10px;
    overflow: hidden;
    height: 40px;
`;

const Tab = styled.div<{ active: boolean }>`
    flex: 1;
    height: 40px;
    text-align: center;
    line-height: 40px;
    background-color: ${(props) => (props.active ? "#9A8C64" : "white")};
    color: ${(props) => (props.active ? "white" : "#A4A4A4")};
    cursor: pointer;
`;
Tab.defaultProps = {
    active: false,
};

const TabSectionContainer = styled.div`
    padding: 20px 20px;
    padding-bottom: 40px;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        padding: 20px 20px;
        padding-bottom: 40px;
    }
`;

const Img = styled.img`
    width: 25px;
    height: 25px;
`;

const ItemTitle = styled.div`
    margin-left: 10px;
    font-size: 15px;
    line-height: 25px;
    color: #333333;
    font-weight: bold;
`;

const ItemInner = styled.div`
    padding: 12px 0px;
`;

const JobCareerPost = styled.div`
    font-size: 16px;
    font-weight: normal;
    color: #a4a4a4;
    margin: 12px 0px;
`;

const JobCareerDescription = styled.div`
    font-size: 15px;
    font-weight: normal;
    color: #333333;
    margin: 12px 0px;
    line-height: 18px;
    white-space: pre-wrap;
`;

const JobCareerRange = styled.div`
    font-size: 15px;
    font-weight: normal;
    color: #333333;
    margin: 12px 0px;
`;

const InfoArea = styled.div`
    border-left: solid 5px #9a8c64;
    padding-left: 25px;
    margin-top: 20px;
    margin-bottom: 10px;
`;

const InfoLabel = styled.div`
    flex-grow: 0;
    flex-shrink: 0;
    font-size: 12px;
    font-weight: bold;
    width: 90px;
    color: #333333;
`;

const PastProjectInfoLabel = styled.div`
    flex-grow: 0;
    flex-shrink: 0;
    font-size: 12px;
    font-weight: bold;
    width: 120px;
    color: #333333;
`;

const InfoValue = styled.div`
    font-size: 12px;
    font-weight: bold;
    padding-right: 10px;
    color: #333333;
`;

const PastProjectDescription = styled.div`
    font-size: 15px;
    font-weight: bold;
    color: #333333;
    margin: 20px 0px;
    white-space: pre-wrap;
    line-height: 24px;
`;

const PastProjectReward = styled.div`
    font-size: 15px;
    font-weight: normal;
    color: #333333;
    margin: 10px 0px;
    white-space: pre-wrap;
`;

const PastProjectInfoContainer = styled.div`
    display: block;
    margin-bottom: 60;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        display: flex;
    }
`;

const PastProjectContent = styled.div`
    display: block;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        display: flex;
    }
`;

const EditButtonContainer = styled.div`
    display: none;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        display: block;
    }
`;

const SubTitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 45px;

    margin-bottom: 15px;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        margin-bottom: 25px;
    }
`;

const SliderWrapper = styled.div`
    margin: 0px auto;
    margin-bottom: 40px;

    // react-slickの横幅の計算がうまく働いていないのでここで決めている感じ
    max-width: ${ConstCalc.CustomerSlideMaxWidthSp};
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        margin-bottom: 50px;
        max-width: ${ConstCalc.CustomerSlideMaxWidthTablet};
    }
    @media screen and (min-width: ${ConstBreakPoint.PcDeskTop}) {
        max-width: ${ConstCalc.CustomerSlideMaxWidthPc};
    }

    // 一部で縦の大きさが変動していたため固定する

    & .slick-slider {
        height: 400px;
    }
`;

const SlideCustomerWrapper = styled.div`
    padding: 5px 15px;
`;

const PmBanner = styled.div`
    width: 100%;
    margin-bottom: 35px;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        width: 400px;
    }
`;

const Indicator = styled.div`
    width: 30px; /*ローディングアイコンの横サイズ*/
    height: 30px; /*ローディングアイコンの縦サイズ*/
    margin: 0 auto;
    margin-top: 20px;
    border-radius: 50%; /*CSS3で円を書く*/
    border: 4px solid ${ConstColor.Corporate}; /*円に○の枠をつける*/
    border-right-color: transparent; /*円の右にC状の空きをつける*/
    animation: spin 1s linear infinite; /*1秒毎にくるくる回転するアニメーション*/
    @keyframes spin {
        /*0%の時は20％の透明度*/
        0% {
            transform: rotate(0deg);
            opacity: 0.2;
        }

        /*50%の時は透明度なし*/
        50% {
            transform: rotate(180deg);
            opacity: 1;
        }

        /*100%の時に20％の透明度に戻る*/
        100% {
            transform: rotate(360deg);
            opacity: 0.2;
        }
    }
`;

const Divider = styled.div`
    border-top: solid 1px #ebf0f1;
    width: 100%;
    height: 1px;
    margin-top: 0px;
    margin-bottom: 30px;
`;

const TagSection = styled.div`
    padding: 32px 20px;
`;

const TagSectionTitle = styled.div`
    font-size: 15px;
    color: #333333;
    font-weight: bold;
    margin-bottom: 7px;
`;

const TagSectionInner = styled.div`
    display: flex;
`;

const TagSectionInnerLeft = styled.div`
    flex: 1;
`;

const TagSectionInnerRight = styled.div`
    flex: 1;
`;

const SingleTagSectionInner = styled.div`
    display: flex;
`;

const SingleTagSectionInnerLeft = styled.div`
    flex: 1;
`;

const SingleTagSectionInnerRight = styled.div`
    flex: 1;
`;

const TagCategory = styled.div`
    font-size: 12px;
    font-weight: bold;
    color: #333333;
    font-weight: bold;
    margin-bottom: 7px;
`;

const TagList = styled.div`
    margin-bottom: 17px;
    display: flex;
`;

const Tag = styled.div`
    display: block;
    color: #9a8c64;
    font-size: 10px;
    font-weight: bold;
    border: solid 1px #9a8c64;
    border-radius: 20px;
    padding: 3px 28px;
    margin-right: 6px;
`;

const ReadMore = styled.div`
    width: 130px;
    height: 30px;
    text-align: center;
    line-height: 30px;
    margin: 18px auto;
    background-color: #eeeeee;
    border-radius: 30px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;
`;

const LinkMenu = styled.div`
    width: 155px;
    position: fixed;
    top: 250px;
    right: 0;
    z-index: 100;
    display: none;
    @media screen and (min-width: ${ConstBreakPoint.PcDeskTop}) {
        display: block;
    }
`;

const ScrollLink = styled.div<{ isActive: boolean }>`
    width: 155px;
    height: 30px;
    margin-bottom: 20px;
    text-align: center;
    cursor: pointer;
    line-height: 30px;
    background-color: #fbfbfb;
    border-radius: 4px;
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
    color: black;
    font-weight: bold;
    font-size: 12px;
    ${({isActive}) => isActive && `background-color: #EEEEEE`};
`;



// WEBPACK FOOTER //
// ./src/presentation/component/client/customer/client_customer_main_v2.tsx